import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useToasts } from "react-toast-notifications";
import { Table, Button, Modal, Form, Breadcrumb, Spinner, Image, Dropdown } from "react-bootstrap";
import useReactRouter from "use-react-router";
import OrderAddModal from "./OrderAddModal";
import "./OrderAddModal.css";
import axios from "axios";
import {
  COLOR_APP,
  COMPLETED,
  CREATED,
} from "../../../constants";
import _ from "lodash";
import { GET_STOCKS_V2 } from "../../../graphql/stock/query";
import { useLazyQuery, useMutation } from "@apollo/client";
import { imageRender, mapCurrency, thousandSeperate } from "../../../helpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faInfoCircle,
  faPlus,
  faTrashCan
} from "@fortawesome/free-solid-svg-icons";
import "./OrderAddV2.css";
import { GET_ORDERGROUPS_DETAIL } from "../../../graphql/order/query";
import {
  CREATE_ORDER_GROUP_V2,
  UPDATE_ORDER_GROUP_V2,
} from "../../../graphql/order/mutation";
import moment from "moment";
import BasicModal from "../../../components/BasicModal";
import Illustration from "../../../image/Illustration.png";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { GET_PRESIGN_URL } from "../../../graphql/presignUrl/query";
import { v4 as uuidv4 } from "uuid";
import { DELETE_S3_FILE } from "../../../graphql/presignUrl/mutation";
import { errorSwal, successSwal } from "../../../helpers/sweetalert";


/**
 * OrderAddV2 Component
 * 
 * A component for creating and editing orders with product selection,
 * customer information, and logistics details.
 */
function OrderAddV2() {
  const { addToast } = useToasts();
  const { history } = useReactRouter();
  const SHOP_ID = localStorage.getItem("SHOP");

  // GraphQL queries and mutations
  const [getOrderData, { data: orderData }] = useLazyQuery(
    GET_ORDERGROUPS_DETAIL,
    { fetchPolicy: "cache-and-network" }
  );

  const [getStocks, { data: stockData }] = useLazyQuery(GET_STOCKS_V2, {
    fetchPolicy: "cache-and-network",
  });
    
  const [createOrderGroupV2, { loading: createOrderGroupV2Loading }] =
    useMutation(CREATE_ORDER_GROUP_V2, {
      onCompleted: (data) => {
        if (data.createOrderGroupV2?.status) {
          successSwal("ສ້າງອໍເດີ້ສຳເລັດ");
          history.goBack();
        } else {
          console.error("Create error:", data.createOrderGroupV2?.msg || "Failed to create order group");
          return errorSwal(data.createOrderGroupV2?.msg || "ເກີດຂໍ້ຜິດພາດໃນການສ້າງອໍເດີ້");
        }
      },
      onError: (error) => {
        console.error("Create error:", error.message);
        return errorSwal(error.message || "ເກີດຂໍ້ຜິດພາດ ກະລຸນາລອງໃໜ່ອີກຄັ້ງ");
      }
    });
    
  const [updateOrderGroupV2, { loading: updateOrderGroupV2Loading }] =
    useMutation(UPDATE_ORDER_GROUP_V2, {
      onCompleted: (data) => {
        if (data.updateOrderGroupV2?.status) {
          successSwal("ສ້າງອໍເດີ້ສຳເລັດ");
          history.goBack();
        } else {
          console.error("Update error:", data.updateOrderGroupV2?.msg || "Failed to update order group");
          return errorSwal(data.updateOrderGroupV2?.msg || "ເກີດຂໍ້ຜິດພາດໃນການສ້າງອໍເດີ້");
        }
      },
      onError: (error) => {
        console.error("Update error:", error.message);
        return errorSwal(error.message || "ເກີດຂໍ້ຜິດພາດ ກະລຸນາລອງໃໜ່ອີກຄັ້ງ");
      }
    });

  // Get presigned URL for file upload
  const [getPreSignUrl] = useLazyQuery(GET_PRESIGN_URL);
  // Delete S3 file mutation
  const [deleteFile] = useMutation(DELETE_S3_FILE, {
    onCompleted: (data) => {
      if (data?.deleteFile?.status) {
        console.log("Old file deleted successfully");
      } else {
        console.error("Error deleting old file:", data?.deleteFile?.msg);
      }
    },
    onError: (error) => {
      console.error("Error deleting old file:", error.message);
    }
  });

  // Component state
  const [isLoading, setIsLoading] = useState(true);
  const [showOrder, setShowOrder] = useState(false);
  const [orderList, setOrderList] = useState([]);
  const [formErrors, setFormErrors] = useState({});

  // Product selection state
  const [cfCode, setCfCode] = useState("");
  const [stockId, setStockId] = useState("");
  const [productName, setProductName] = useState("");
  const [currency, setCurrency] = useState("ກີບ");
  const [amount, setAmount] = useState(1);
  const [price, setPrice] = useState("");
  const [originPrice, setOriginPrice] = useState("");
  const [totalPrice, setTotalPrice] = useState("");
  const [stocksData, setStocksData] = useState([]);
  const [stockAmount, setStockAmount] = useState("");
  
  // Order summary state
  const [sumPriceLak, setSumPriceLak] = useState(0);
  const [sumPriceUsd, setSumPriceUsd] = useState(0);
  const [sumPriceBaht, setSumPriceBaht] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  
  // Customer information state
  const [cfName, setCfName] = useState("");
  const [note, setNote] = useState("");
  const [phonePrefix, setPhonePrefix] = useState("020");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [addressCf, setAddressCf] = useState("");
  
  // Payment and shipping state
  const [payment, setPayment] = useState("TRANSFER");
  const [paymentType, setPaymentType] = useState("DESTINATION");
  const [check, setCheck] = useState(true);
  const [billCode, setBillCode] = useState("");
  const [orderGroupStatus, setOrderGroupStatus] = useState(CREATED);
  const [shipPrice, setShipPrice] = useState("");
  const [logistic, setLogistic] = useState("");
  const [isPaymented, setIsPaymented] = useState(false);
  
  // UI control state
  const [editMode, setEditMode] = useState(false);
  const [selectedOrderGroupData, setSelectedOrderGroupData] = useState(null);
  const [createdAt, setCreatedAt] = useState(moment());
  const [showDeleteStockConfirm, setShowDeleteStockConfirm] = useState(false);
  const [selectedStock, setSelectedStock] = useState({});
  
  // File upload state
  const [file, setFile] = useState(null);
  const [filename, setFilename] = useState(null);
  const [paySlip, setPaySlip] = useState(null);
  const [paySlipPreview, setPaySlipPreview] = useState("");
  const [showImageModal, setShowImageModal] = useState(false);

  // Initialize component data
  useEffect(() => {
    getUserStock();
    const isEditMode = history.location.state?.editMode;
    const orderGroupData = history.location.state?.orderGroup;
    
    setEditMode(isEditMode);
    setSelectedOrderGroupData(orderGroupData);
    
    // Reset form errors when component mounts
    setFormErrors({});
  }, []);

  // Load order data in edit mode
  useEffect(() => {
    if (editMode && selectedOrderGroupData) {
      getOrderGroupDetail();
    } else {
      setIsLoading(false);
    }
  }, [editMode, selectedOrderGroupData]);

  // Update payment type based on check state
  useEffect(() => {
    setPaymentType(check ? "DESTINATION" : "ORIGINAL");
  }, [check]);

  // Update isPaymented when payment type changes
  useEffect(() => {
    if (payment === "CASH") {
      setIsPaymented(true);
      setOrderGroupStatus("COMPLETED");
    }
  }, [payment]);

  // Update stocks data when stock data is fetched
  useEffect(() => {
    const v2 = stockData?.stocksV2?.data;
    if (v2 && v2.length > 0) {
      setStocksData(v2);
    }
  }, [stockData]);

  // Calculate total price when price changes
  useEffect(() => {
    if (price && amount) {
      const _total = parseFloat(price) * parseInt(amount);
      setTotalPrice(_total);
    }
  }, [price, amount]);

  // Calculate order summary values
  useMemo(() => {
    if (orderList && orderList.length > 0) {
      let _sumPriceLak = 0;
      let _sumPriceBaht = 0;
      let _sumPriceUsd = 0;
      let qty = 0;

      orderList.forEach(order => {
        if (["BAHT", "ບາດ"].includes(order?.currency)) {
          _sumPriceBaht += parseFloat(order.totalPrice || 0);
        } else if (["USD", "ໂດລາ"].includes(order?.currency)) {
          _sumPriceUsd += parseFloat(order.totalPrice || 0);
        } else {
          _sumPriceLak += parseFloat(order.totalPrice || 0);
        }
        qty += parseInt(order.amount || 0);
      });

      setSumPriceLak(_sumPriceLak);
      setSumPriceBaht(_sumPriceBaht);
      setSumPriceUsd(_sumPriceUsd);
      setTotalAmount(qty);
    } else {
      setSumPriceLak(0);
      setSumPriceBaht(0);
      setSumPriceUsd(0);
      setTotalAmount(0);
    }
  }, [orderList]);

  // Validate form before submission
  const validateForm = useCallback(() => {
    const errors = {};
    
    if (orderList?.length <= 0) {
      errors.orderList = "ເພີ່ມສິນຄ້າໃດຫນຶ່ງກ່ອນ!";
    }
    
    if (_.isEmpty(phone)) {
      errors.phone = "ກະລຸນາປ້ອນເບີໂທລູກຄ້າກ່ອນ!";
    } else if (phonePrefix === "020" && !/^\d{8}$/.test(phone)) {
      errors.phone = "ເບີໂທ 020 ຕ້ອງມີ 8 ຕົວເລກ!";
    } else if (phonePrefix === "030" && (phone.length < 7 || phone.length > 8)) {
      errors.phone = "ເບີໂທ 030 ຕ້ອງມີ 7-8 ຕົວເລກ!";
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [orderList, phone]);

  const getOrderGroupDetail = async () => {
    try {
      setIsLoading(true);
      const _orderData = await getOrderData({
        variables: {
          orderBy: "createdAt_DESC",
          where: {
            id: selectedOrderGroupData?.id,
          },
        },
      });

      setBillCode(_orderData?.data?.orderGroup?.code);
      setCfName(
        _orderData?.data?.orderGroup?.customerName != "" &&
          _orderData?.data?.orderGroup?.customerName
          ? _orderData?.data?.orderGroup?.customerName
          : _orderData?.data?.orderGroup?.cfName
      );
      
      const loadedPhone = _orderData?.data?.orderGroup?.phone || "";
      if (loadedPhone.startsWith("030")) {
        setPhonePrefix("030");
        setPhone(loadedPhone.substring(3));
      } else if (loadedPhone.startsWith("020")) {
        setPhonePrefix("020");
        setPhone(loadedPhone.substring(3));
      } else {
        setPhonePrefix("020");
        setPhone(loadedPhone);
      }
      
      setLogistic(_orderData?.data?.orderGroup?.logistic);
      setAddressCf(_orderData?.data?.orderGroup?.addressCf);
      setNote(_orderData?.data?.orderGroup?.note);
      setShipPrice(_orderData?.data?.orderGroup?.shipPrice);
      setOrderGroupStatus(_orderData?.data?.orderGroup?.type);
      setPaymentType(_orderData?.data?.orderGroup?.paymentType);
      setCreatedAt(_orderData?.data?.orderGroup?.createdAt);
      setOrderGroupStatus(_orderData?.data?.orderGroup?.status);
      setPayment(_orderData?.data?.orderGroup?.payment);
      setCheck(_orderData?.data?.orderGroup?.paymentType === "DESTINATION");
      setOrderList(_orderData?.data?.orderGroup?.orders);
      setIsLoading(false);
      // Removed undefined setParcelName call
      
      // Set isPaymented based on the order data or payment type
      if (_orderData?.data?.orderGroup?.isPaymented !== undefined) {
        setIsPaymented(_orderData?.data?.orderGroup?.isPaymented);
      } else if (_orderData?.data?.orderGroup?.payment === "CASH") {
        setIsPaymented(true);
      }
      if (_orderData?.data?.orderGroup?.paySlip) {
        setPaySlip(_orderData?.data?.orderGroup?.paySlip);
        const slipUrl = imageRender(_orderData?.data?.orderGroup?.paySlip);
        setPaySlipPreview(slipUrl);
      }
    } catch (error) {
      console.log("get order group error: ", error);
    }
  };

  const getUserStock = async () => {
    try {
      await getStocks({
        variables: {
          where: {
            isDeleted: false,
            shop: SHOP_ID,
          },
          orderBy: "createdAt_DESC",
          limit: 1000,
          skip: 0,
        },
      });
    } catch (error) {
      console.log("Error retrieving posts", error);
    }
  };

  const onSelectStock = async (e) => {
    const value = e?.target?.value || "";
    const trimmedValue = value.trim();
    
    if (!trimmedValue) return;

    let searchCode = trimmedValue;
    
    // Handle "ລະຫັດ: CODE" format from datalist (handle both with and without space)
    if (trimmedValue.includes("ລະຫັດ:")) {
       const parts = trimmedValue.split("ລະຫັດ:");
       if (parts.length > 1) {
           searchCode = parts[1].trim();
       }
    }

    // Use case-insensitive comparison and handle potential non-string values
    const checkStock = stocksData.find((stock) => {
        const stockCode = String(stock.cfMessage || "").trim().toLowerCase();
        const search = String(searchCode).trim().toLowerCase();
        return stockCode === search;
    });

    if (checkStock) {
      setCfCode(checkStock.cfMessage);
      setCurrency(checkStock.currency);
      setProductName(checkStock.name);
      setPrice(checkStock.price);
      setOriginPrice(checkStock.originPrice);
      setStockId(checkStock.id);
      setStockAmount(checkStock.amount);
    }
  };

  const onSelectStockItem = (stock) => {
    if (!stock) return;
    setCfCode(stock?.cfMessage || "");
    setCurrency(stock?.currency);
    setProductName(stock?.name);
    setPrice(stock?.price);
    setOriginPrice(stock?.originPrice);
    setStockId(stock?.id);
    setStockAmount(stock?.amount);
  };

  /**
   * Add a product to the order list
   * Validates product data before adding to the list
   */
  const _createOrderList = () => {
    try {
      // Validate product selection
      if (!cfCode) {
        errorSwal("ເລືອກສິນຄ້າໃດຫນຶ່ງກ່ອນ!");
        return;
      }
      
      if (!productName) {
        errorSwal("ກະລຸນາປ້ອນຊື່ສິນຄ້າ");
        return;
      }
      
      if (!price || isNaN(parseFloat(price)) || parseFloat(price) <= 0) {
        errorSwal("ກະລຸນາປ້ອນລາຄາທີ່ຖືກຕ້ອງ");
        return;
      }
      
      if (!amount || isNaN(parseInt(amount)) || parseInt(amount) <= 0) {
        errorSwal("ກະລຸນາປ້ອນຈຳນວນທີ່ຖືກຕ້ອງ");
        return;
      }

      // Check for duplicate product by stockId
      const isDuplicate = orderList.some(product => product.stock === stockId);
      if (isDuplicate) {
        errorSwal("ສິນຄ້ານີ້ມີໃນລາຍການແລ້ວ");
        return;
      }

      // Create new product entry
      const newProduct = {
        cfMessage: cfCode,
        currency,
        productName,
        amount: parseInt(amount),
        price: parseFloat(price),
        totalPrice: parseFloat(totalPrice || (price * amount)),
        originPrice: parseFloat(originPrice || 0),
        id: stockId,
        stock: stockId,
        stockAmount: parseInt(stockAmount || 0),
      };

      // Add to order list
      setOrderList([...orderList, newProduct]);
      
      // Reset product form
      setShowOrder(false);
      setCfCode("");
      setProductName("");
      setPrice("");
      setOriginPrice("");
      setTotalPrice("");
      setAmount(1);
      setStockId("");
      setStockAmount("");
    } catch (error) {
      console.error("Error adding product to order:", error);
      errorSwal("ເກີດຂໍ້ຜິດພາດໃນການເພີ່ມສິນຄ້າ");
    }
  };


  /**
   * Save order data
   * Validates form, creates or updates order, and handles logistics
   */
  const _saveData = async () => {
    try {
      // Check if any operations are in progress
      if (
        createOrderGroupV2Loading ||
        updateOrderGroupV2Loading 
      ) {
        return;
      }

      // Validate form before submission
      if (!validateForm()) {
        // Display the first error message
        const firstError = Object.values(formErrors)[0];
        errorSwal(firstError);
        return;
      }

      // Perform update or create operation based on edit mode
      let success = false;
      
      if (editMode) {
        success = await _updateOrderGroup();
      } else {
        success = await _createOrderGroup();
      }
      
      // If order creation/update failed, don't proceed
      if (!success) {
        return;
      }

      // Show success message
      addToast(
        editMode ? "ແກ້ໄຂອໍເດີ້ສຳເລັດ!" : "ສ້າງອໍເດີ້ສຳເລັດ!", 
        {
          appearance: "success",
          autoDismiss: true,
        }
      );

      // Reset form fields
      _resetFormFields();

      // Navigate back to orders list
      history.goBack();
    } catch (error) {
      console.error("Error saving order:", error);
      addToast("ເກີດຂໍ້ຜິດພາດໃນການບັນທຶກຂໍ້ມູນ. ກະລຸນາລອງອີກຄັ້ງ", {
        appearance: "error",
        autoDismiss: true,
      });
    }
  };
  
  /**
   * Reset form fields after successful submission
   */
  const _resetFormFields = () => {
    setShowOrder(false);
    setCfCode("");
    setPrice(0);
    setOriginPrice(0);
    setTotalPrice(0);
    setAmount(1);
    setProductName("");
  };


  /**
   * Create an order group from individual orders
   * @param {Array<string>} orderData - Array of order IDs
   * @returns {Promise<boolean>} Success status
   */
  const _createOrderGroup = async () => {
    try {

      // Check if operation is in progress
      if (createOrderGroupV2Loading) return false;
      
      if (!phone) {
        errorSwal("ກະລຸນາປ້ອນເບີໂທລູກຄ້າກ່ອນ");
        return;
      }
      if (!cfName) {
        errorSwal("ກະລຸນາປ້ອນຊື່ກ່ອນ");
        return;
      }
      if (!payment) {
        errorSwal("ກະລຸນາເລືອກປະເພດການຈ່າຍກ່ອນ");
        return;
      }

      const status = payment === "CASH" ? COMPLETED : orderGroupStatus || CREATED

      // Map new order
      const newOrderGroup = orderList.map((order) => {
        const currency = mapCurrency(order?.currency)
        return {
          type: "KEY_IN",
          amount: parseInt(order.amount),
          originPrice: parseFloat(order.originPrice),
          price: parseFloat(order.price),
          totalPrice: parseFloat(order.totalPrice),
          phone: phonePrefix + phone,
          cfName,
          shop: SHOP_ID,
          currency,
          cfMessage: order?.cfMessage,
          productName: order?.productName,
          stock: order?.stock,
          status
        };
      });

      let newPaySlip = "";

      // If there's a file to upload, get presigned URL and upload it first
      if (file && filename) {
        try {
          const preSignUrlData = await getPreSignUrl({ variables: { name: filename } });
          if (preSignUrlData?.data?.preSignedUrl?.url) {
            const _imageUrl = await onFileUpload(preSignUrlData.data.preSignedUrl.url);
            // Extract just the filename from the full URL
            // This handles any S3 URL format by taking just the filename after the last slash
            if (!_imageUrl) return;
            const _filename = _imageUrl.split('/').pop();
            newPaySlip = _filename;
          }
        } catch (error) {
          console.error("Error getting presigned URL:", error);
          return "ເກີດຂໍ້ຜິດພາດໃນການອັບໂຫລດຮູບພາບ";
        }
      }

      const data = {
        orders: newOrderGroup,
        orderGroup: {
          type: "KEY_IN",
          // orders: orderData,
          amount: parseInt(totalAmount) || 0,
          totalPrice: parseInt(sumPriceLak) || 0,
          sumPriceBaht: sumPriceBaht || 0,
          sumPriceUsd: sumPriceUsd || 0,
          customerName: cfName || "",
          shop: SHOP_ID,
          phone: phonePrefix + phone,
          addressCf: address || "",
          note: note || "",
          payment: payment || "",
          paymentType: paymentType || "DESTINATION",
          status,
          logistic: logistic || "",
          addressCf: addressCf || "",
          paySlip: newPaySlip || null,
          isPaymented: isPaymented,
        },
      }
      
      // Create order group
      createOrderGroupV2({
        variables: {
          data: data
        },
      });
      
    } catch (error) {
      console.error("Error creating order group:", error);
      errorSwal("ເກີດຂໍ້ຜິດພາດທີ່ບໍ່ຄາດຄີດໃນການສ້າງອໍເດີ້");
    }
  };

  const _updateOrderGroup = async () => {
    try {
      if (updateOrderGroupV2Loading) return;
      let newPaySlip = "";

      // If there's a file to upload, get presigned URL and upload it first
      if (file && filename) {
        try {
          const preSignUrlData = await getPreSignUrl({ variables: { name: filename } });
          if (preSignUrlData?.data?.preSignedUrl?.url) {
            const _imageUrl = await onFileUpload(preSignUrlData.data.preSignedUrl.url);
            // Extract just the filename from the full URL
            // This handles any S3 URL format by taking just the filename after the last slash
            if (!_imageUrl) return;
            const _filename = _imageUrl.split('/').pop();
            newPaySlip = _filename;
            
            // If user already had an image, delete the old one
            if (paySlip) {
              try {
                // Extract just the filename from the old image path
                const oldFilename = paySlip.split('/').pop();
                if (oldFilename) {
                  deleteFile({
                    variables: {
                      filename: oldFilename
                    }
                  });
                }
              } catch (deleteError) {
                console.error("Error deleting old image:", deleteError);
                // Continue with the update even if deletion fails
              }
            }
          }
        } catch (error) {
          console.error("Error getting presigned URL:", error);
          return "ເກີດຂໍ້ຜິດພາດໃນການອັບໂຫລດຮູບພາບ";
        }
      } else {
        newPaySlip = paySlip;
      }

      if (!phone) {
        errorSwal("ກະລຸນາປ້ອນເບີໂທລູກຄ້າກ່ອນ");
        return;
      }

      if (!cfName) {
        errorSwal("ກະລຸນາປ້ອນຊື່ກ່ອນ");
        return;
      }
      
      if (!payment) {
        errorSwal("ກະລຸນາເລືອກປະເພດການຈ່າຍກ່ອນ");
        return;
      }

      const status = payment === "CASH" ? COMPLETED : orderGroupStatus || CREATED

      // Map new order
      const newOrderGroup = orderList.map((order) => {
        const currency = mapCurrency(order?.currency)
        return {
          type: "KEY_IN",
          amount: parseInt(order.amount),
          originPrice: parseFloat(order.originPrice),
          price: parseFloat(order.price),
          totalPrice: parseFloat(order.totalPrice),
          phone: phonePrefix + phone,
          cfName,
          shop: SHOP_ID,
          currency,
          cfMessage: order?.cfMessage,
          productName: order?.productName,
          stock: typeof order?.stock === "object" ? order?.stock?.id : order?.stock,
          status
        };
      });

      const data = {
        orders: newOrderGroup,
        orderGroup: {
          type: "KEY_IN",
          // orders: orderData,
          amount: parseInt(totalAmount) || 0,
          totalPrice: parseInt(sumPriceLak) || 0,
          sumPriceBaht: sumPriceBaht || 0,
          sumPriceUsd: sumPriceUsd || 0,
          customerName: cfName || "",
          shop: SHOP_ID,
          phone: phonePrefix + phone,
          addressCf: address || "",
          note: note || "",
          payment: payment || "",
          paymentType: paymentType || "DESTINATION",
          status,
          logistic: logistic || "",
          addressCf: addressCf || "",
          paySlip: newPaySlip || null,
          isPaymented: isPaymented,
        },
      }

      updateOrderGroupV2({
        variables: {
          data: data,
          where: {
            id: selectedOrderGroupData?.id,
          },
        },
      });
      // console.log("createOrderGroup Res: ", _resOrderGroup)
    } catch (error) {
      console.log("createOrderGroup Error: ", error);
    }
  };

  const handleDelete = () => {
    let _orderList = [...orderList];
    _.remove(_orderList, { id: selectedStock?.id });
    setOrderList(_orderList);
    setShowDeleteStockConfirm(false);
  };

  // Handle file selection - only for preview
  const handleFileChange = (event) => {
    const _file = event.target.files[0]; // Get the selected file
    if (_file) {
      setFile(_file);
      
      // Generate a unique filename using uuidv4 and the file extension
      const ext = _file.name.split('.').pop();
      const uniqueFilename = `payment-slip-${uuidv4()}.${ext}`;
      setFilename(uniqueFilename);
      setPaySlip(uniqueFilename);

      // File preview logic
      const reader = new FileReader();
      reader.onloadend = () => {
        setPaySlipPreview(reader.result); // Set preview image
      };
      reader.readAsDataURL(_file); // Read file as a Data URL for preview
    }
  };

  // Upload file to S3 using presigned URL
  const onFileUpload = async (url) => {
    try {
      if (file && url) {
        await axios.put(url, file, {
          headers: {
            "Content-Type": "file/*; image/*",
            Accept: "application/json",
            "Access-Control-Allow-Origin": "*",
            "Access-Control-Allow-Methods": "DELETE, POST, GET, OPTIONS",
            "Access-Control-Allow-Headers":
              "Content-Type, Access-Control-Allow-Headers, Authorization, X-Requested-With",
          },
        });
        
        // Update form data with the S3 URL (removing query parameters)
        const baseUrl = url.split('?')[0];
        return baseUrl;
      }
      return null;
    } catch (error) {
      console.error("Error uploading image:", error);
      return null;
    }
  };

  if (isLoading) return <LoadingSpinner />;
  return (
    <div>
      <BasicModal
        show={showDeleteStockConfirm}
        onHide={() => setShowDeleteStockConfirm(false)}
        okay={handleDelete}
        title="ທ່ານຕ້ອງການລຶບຂໍ້ມູນ ຫຼື ບໍ່"
        leftButtonName="ຍົກເລີກ"
        rightButtonName="ຕົກລົງ"
        Illustration={Illustration}
      />
        <div className="container-fluid px-3">
          <Breadcrumb className="py-2">
            <Breadcrumb.Item onClick={() => history.goBack()}>
              ຈັດການການສັ່ງຊື້
            </Breadcrumb.Item>
            <Breadcrumb.Item active>
              {editMode ? "ແກ້ໄຂການສັ່ງຊື້" : "ເພີ່ມການສັ່ງຊື້"}
            </Breadcrumb.Item>
          </Breadcrumb>

          <div className="mb-1">
            <h4 style={{ fontWeight: "bold" }}>
              {editMode ? "ແກ້ໄຂອໍເດີ້" : "ສ້າງອໍເດີ້"} {editMode && " " + billCode || ""}
            </h4>

            <div
              style={{ borderBottom: "2px solid #D7DBE3", marginBottom: 5 }}
            />

            <div className="row">
              <div className="col-lg-4 col-md-6 col-sm-12 mb-1">
                <h5 style={{ fontWeight: "bold" }}>ຂໍ້ມູນບີນ</h5>
                {/* <Form.Group className="mb-3">
                  <Form.Label
                    style={{
                      marginBottom: 2,
                      fontWeight: "bold",
                      color: "#5C5C5C",
                    }}>
                    ແລກບິນ
                  </Form.Label>
                  <Form.Control
                    // type="text"
                    placeholder="K......."
                    value={billCode}
                    readOnly={true}
                    style={{
                      padding: "10px",
                      width: "100%",
                      height: "42px",
                      borderRadius: "2px",
                      alignItems: "center",
                      top: "10px",
                      color: "#8C8C8C",
                      border: "1px solid #E0E0E0",
                      background: "#F5F5F5",
                    }}
                  />
                  <p
                    style={{
                      paddingTop: "10px",
                      color: "#e69138",
                      fontSize: 12,
                      fontWeight: 400,
                    }}>
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      style={{ color: "#e69138" }}
                    />{" "}
                    ລະບົບຈະສ້າງເລກທີໃບສັ່ງຊື້ໃຫ້ເອງ
                  </p>
                </Form.Group> */}
                
                <Form.Group className="mb-3">
                  <Form.Label style={{
                      marginBottom: 2,
                      fontWeight: "bold",
                      color: "#5C5C5C",
                    }}>ວັນທິສັ່ງຊື້</Form.Label>
                  <Form.Control
                    value={moment(createdAt).format("DD/MM/YYYY, HH:mm A")}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <label style={{
                      marginBottom: 2,
                      fontWeight: "bold",
                      color: "#5C5C5C",
                    }}>ສະຖານະອໍເດີ້</label>
                  <Form.Select
                    onChange={(e) => setOrderGroupStatus(e.target.value)}
                    className="form-control"
                    style={{
                      fontSize: "16px",
                      whiteSpace: "nowrap",
                    }}
                    value={orderGroupStatus}
                    defaultValue={"CREATED"}
                    aria-label="Default select example"
                  >
                    <option selected value="">
                      --ທັງຫມົດ--
                    </option>
                    <option value="CREATED">ອໍເດີເຂົ້າ</option>
                    <option value="READY_TO_SHIPPING">ອໍເດີພ້ອມສົ່ງ</option>
                    <option value="SHIPPING">ອໍເດີຢູ່ໃນລະຫວ່າງຈັດສົ່ງ</option>
                    <option value="COMPLETED">ອໍເດີສຳເລັດ</option>
                    <option value="CANCELLED">ປະຫວັດຍົກເລິກ</option>
                    <option value="IMAGE_NOT_YET_PAID">ແຈ້ງຍອດແຕ່ບໍ່ໂອນ</option>
                    <option value="PAYMENT_STILL_INCOMPLETE">ຍອດເງິນບໍ່ຖຶກຕ້ອງ</option>
                    <option value="WAITINGCONFIRM">ລໍຖ້າການຢືນຢັນ</option>
                    <option value="USERCONFIRM">ກວດສອບແລ້ວ</option>
                    <option value="PRINT_ORDER">ພີມບິນແລ້ວ</option>
                    <option value="SHIPPING_COMPLETED">ສົ່ງສຳເລັດ</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={{
                      marginBottom: 2,
                      fontWeight: "bold",
                      color: "#5C5C5C",
                    }}>ປະເພດການຈ່າຍ</Form.Label>
                  <Form.Select
                    name="payment"
                    as="select"
                    value={payment}
                    onChange={(e) => setPayment(e.target.value)}>
                    <option value="">ເລືອກປະເພດການຈ່າຍ</option>
                    {[
                      { id: 1, value: "CASH", name: "ຈ່າຍສົດ" },
                      { id: 2, value: "TRANSFER", name: "ໂອນ" },
                    ].map((paymentOption, pIndex) => (
                      <option value={paymentOption?.value} key={"payment" + pIndex}>
                        {paymentOption?.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label style={{
                      marginBottom: 2,
                      fontWeight: "bold",
                      color: "#5C5C5C",
                    }}>ສະຖານະການຊຳລະ</Form.Label>
                  <div className="d-flex align-items-center">
                    <div className="form-check">
                      <input
                        className="form-check-input" 
                        type="checkbox"
                        id="payment-status-checkbox"
                        checked={isPaymented}
                        onChange={(e) => setIsPaymented(e.target.checked)}
                        disabled={payment === "CASH"}
                        style={{ width: '20px', height: '20px', cursor: payment === "CASH" ? 'not-allowed' : 'pointer' }}
                      />
                      <label 
                        className="form-check-label" 
                        htmlFor="payment-status-checkbox"
                        style={{ marginLeft: '8px', cursor: payment === "CASH" ? 'not-allowed' : 'pointer' }}
                      >
                        ຈ່າຍເງິນແລ້ວ
                      </label>
                    </div>
                  </div>
                  {payment === "CASH" && (
                    <div className="text-muted mt-2" style={{ fontSize: '0.875rem' }}>
                      <i className="fa fa-info-circle me-1"></i>
                      ການຊຳລະດ້ວຍເງິນສົດຈະຖືກຕັ້ງເປັນຈ່າຍແລ້ວໂດຍອັດຕະໂນມັດ
                    </div>
                  )}
                </Form.Group>

              </div>
              <div className="col-lg-4 col-md-6 col-sm-12 mb-1"> {/* ຂໍ້ມູນລູກຄ້າ */}
                <h5 style={{ fontWeight: "bold" }}>ຂໍ້ມູນລູກຄ້າ</h5>
                <Form.Group className="mb-3">
                  <Form.Label
                    style={{
                      marginBottom: 2,
                      fontWeight: "bold",
                      color: "#5C5C5C",
                    }}>
                    ຊື່ລູກຄ້າ
                  </Form.Label>
                  <Form.Control
                    name="cfName"
                    type="text"
                    placeholder="ກະລຸນາປ້ອນຊື່ລູກຄ້າ"
                    value={cfName}
                    onChange={(e) => setCfName(e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label
                    style={{
                      marginBottom: 2,
                      fontWeight: "bold",
                      color: "#5C5C5C",
                    }}>
                    ເບີໂທ
                    <span style={{ marginLeft: 1, color: "#FF3939" }}>*</span>
                  </Form.Label>
                  <div style={{ position: "relative" }}>
                    <div
                      style={{
                        position: "absolute",
                        left: "5px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        zIndex: 10,
                      }}>
                      <Dropdown onSelect={(key) => setPhonePrefix(key)}>
                        <Dropdown.Toggle
                          variant="link"
                          id="dropdown-phone-prefix"
                          style={{
                            padding: 0,
                            textDecoration: "none",
                            color: "#5C5C5C",
                            fontSize: 17,
                            display: "flex",
                            alignItems: "center",
                          }}>
                          {phonePrefix}
                        </Dropdown.Toggle>
                        <Dropdown.Menu style={{ minWidth: "60px" }}>
                          <Dropdown.Item eventKey="020">020</Dropdown.Item>
                          <Dropdown.Item eventKey="030">030</Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                    <Form.Control
                      style={{
                        paddingLeft: "3.5em",
                      }}
                      name="phone"
                      type="text"
                      placeholder="xxx-xxx-xx"
                      value={phone}
                      onChange={(e) => {
                        const _phone = e?.target?.value;

                        // Check if the input is a number and has at most 8 digits
                        if (!/^\d{0,8}$/.test(_phone)) return;

                        setPhone(_phone);
                      }}
                    />
                  </div>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label
                    style={{
                      marginBottom: 2,
                      fontWeight: "bold",
                      color: "#5C5C5C",
                    }}>
                    ຂົນສົ່ງ
                  </Form.Label>
                  <Form.Control
                    name="logistic"
                    type="text"
                    placeholder="ພິມຂົນສົ່ງ..."
                    value={logistic}
                    onChange={(e) => setLogistic(e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label
                    style={{
                      marginBottom: 2,
                      fontWeight: "bold",
                      color: "#5C5C5C",
                    }}>
                    ທີ່ຢູ່ປາຍທາງ
                  </Form.Label>
                  <Form.Control
                    name="addressCf"
                    type="text"
                    as="textarea"
                    placeholder="ຕົວຢ່າງ: ຮ່ອມ, ບ້ານ, ເມືອງ, ແຂວງ..."
                    value={
                      addressCf ||
                      orderData?.orderGroup?.destinationLogistic
                    }
                    onChange={(e) => setAddressCf(e.target.value)}
                  />
                </Form.Group>
              </div>
              <div className="col-lg-4 col-md-6 col-sm-12 mb-1"> {/* ສະລິການຊໍາລະ */}
                <h5 style={{ fontWeight: "bold" }}>ສະລິບການຊໍາລະ</h5>
                <Form.Group className="mb-3">
                  <Form.Label
                    style={{
                      marginBottom: 2,
                      fontWeight: "bold",
                      color: "#5C5C5C",
                    }}>
                    ຮູບການຊໍາລະ
                  </Form.Label>
                  {(file || paySlipPreview) ? (
                    <div className="file-preview-container" style={{ position: 'relative', marginTop: 10, width: '100%', height: '200px', overflow: 'hidden', borderRadius: '8px', border: '1px solid #eee' }}>
                      <img
                        src={paySlipPreview}
                        alt="Payment slip preview"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          cursor: "pointer",
                          backgroundColor: "#f8f8f8"
                        }}
                        onClick={() => setShowImageModal(true)}
                      />
                      <button 
                        onClick={
                          () => {
                            setFile(null);
                            setFilename(null);
                            setPaySlip(null);
                            setPaySlipPreview(null);
                          }
                        }
                        style={{
                          position: 'absolute',
                          top: '10px',
                          right: '10px',
                          background: 'rgba(255,255,255,0.8)',
                          borderRadius: '50%',
                          width: '36px',
                          height: '36px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          color: '#ff4d4f',
                          border: 'none',
                          padding: 0
                        }}
                      >
                        <i className="fa fa-trash" style={{ fontSize: '16px' }}></i>
                      </button>
                    </div>
                  ) : (
                    <div className="file-upload-container">
                      <label 
                        htmlFor="payment-slip-upload" 
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '2px dashed #d9d9d9',
                          borderRadius: '8px',
                          padding: '20px',
                          cursor: 'pointer',
                          backgroundColor: '#fafafa',
                          transition: 'all 0.3s ease',
                          marginTop: 10,
                          minHeight: '120px',
                          width: '100%',
                          touchAction: 'manipulation'
                        }}
                      >
                        <i className="fa fa-cloud-upload" style={{ fontSize: '24px', color: '#bfbfbf', marginBottom: '8px' }}></i>
                        <span style={{ color: '#8c8c8c', fontSize: '14px', textAlign: 'center' }}>ເພີ່ມຮູບສະລິບການໂອນເງິນ</span>
                        <span style={{ color: '#8c8c8c', fontSize: '12px', marginTop: '5px', textAlign: 'center' }}>ຄລິກເພື່ອເລືອກຮູບ</span>
                      </label>
                      <input
                        id="payment-slip-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                      />
                    </div>
                  )}
                  <Form.Text className="text-muted mt-2">
                    ອັບໂຫລດຮູບສະລິບການໂອນເງິນ (ຮອງຮັບຮູບແບບ JPG, PNG, GIF)
                  </Form.Text>
                </Form.Group>
                
                {/* Image Preview Modal */}
                <Modal
                  show={showImageModal}
                  onHide={() => setShowImageModal(false)}
                  centered
                  size="lg"
                  dialogClassName="responsive-modal"
                >
                  <Modal.Header closeButton>
                    <Modal.Title className="w-100 text-center">ຮູບສະລິບການໂອນເງິນ</Modal.Title>
                  </Modal.Header>
                  <Modal.Body className="p-2">
                    <div className="d-flex justify-content-center align-items-center">
                      <Image
                        src={paySlipPreview}
                        alt="Payment Slip"
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '70vh',
                          objectFit: 'contain',
                          borderRadius: '4px'
                        }}
                        fluid
                      />
                    </div>
                  </Modal.Body>
                  <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowImageModal(false)}>
                      ປິດ
                    </Button>
                  </Modal.Footer>
                </Modal>
              </div>
            </div>
          </div>

          <div className="row align-items-center mt-1 mb-1">
            <div className="col-sm-6 text-start">
              <h5 style={{ fontWeight: "bold" }}>ລາຍການສິນຄ້າ</h5>
            </div>
            <div className="col-sm-6 text-end">
              <Button
                variant="primary"
                style={{
                  background: COLOR_APP,
                  borderColor: "transparent",
                  borderRadius: "8px",
                  padding: "8px 15px",
                  fontWeight: "500",
                  boxShadow: "0 4px 10px rgba(26,102,190,0.3)",
                  marginBottom: "1px"
                }}
                onClick={() => setShowOrder(true)}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" /> ເພີ່ມລາຍການ
              </Button>
            </div>
          </div>
          <div className="table-responsive">
            <Table>
              <thead style={{ background: "#F3F4F6" }}>
                <tr>
                  <th style={{ border: "none" }}>#</th>
                  <th style={{ border: "none" }}>ຮູບສິນຄ້າ</th>
                  <th style={{ border: "none" }}>ລະຫັດ CF</th>
                  <th style={{ border: "none" }}>ຊື່ສິນຄ້າ</th>
                  <th style={{ border: "none" }}>ຈຳນວນ</th>
                  <th style={{ border: "none" }}>ລາຄາ</th>
                  <th style={{ border: "none" }}>ທັງໝົດ</th>
                  <th style={{ border: "none" }}>ຈັດການ</th>
                </tr>
              </thead>
            <tbody>
              {orderList?.map((order, index) => (
                <tr key={index}>
                  <td style={{ borderBottom: "none" }}>{index + 1}</td>
                  <td style={{ borderBottom: "none" }}>
                    <img
                      alt=""
                      src={imageRender(
                        order?.liveStock?.image ??
                        order?.postStock?.image ??
                        order?.stock?.image
                      )}
                      style={{
                        margin: 0,
                        padding: 0,
                        objectFit: "cover",
                        maxHeight: "40px",
                      }}
                    />
                  </td>
                  <td style={{ borderBottom: "none" }}>{order?.cfMessage || order?.stock?.cfMessage}</td>
                  <td style={{ borderBottom: "none" }}>{order?.productName}</td>
                  <td style={{ borderBottom: "none" }}>
                    {/* <FontAwesomeIcon color={COLOR_APP} className="mr-2" icon={faMinusSquare} /> */}
                    {order?.amount}
                    {/* <FontAwesomeIcon color={COLOR_APP} className="ml-2" icon={faPlusSquare} /> */}
                  </td>
                  <td style={{ borderBottom: "none" }}>
                    {thousandSeperate(order?.price ?? 0)}{" "}
                    {order?.currency ?? "ກີບ"}
                  </td>
                  <td style={{ borderBottom: "none" }}>
                    {thousandSeperate(order?.totalPrice ?? 0)}{" "}
                    {order?.currency ?? "ກີບ"}
                  </td>
                  <td style={{ borderBottom: "none" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "start",
                        alignItems: "center",
                        padding: 6,
                        color: "red",
                        cursor: "pointer",
                        transition: "all 0.3s ease-in-out",
                      }}
                      onClick={() => {
                        setSelectedStock(order);
                        setShowDeleteStockConfirm(true);
                      }}>
                      <FontAwesomeIcon className="me-2" icon={faTrashCan} />
                      ລົບ
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            </Table>
          </div>
          <div className="row">
            <div className="col-md-6 col-sm-12"></div>
            <div className="col-md-6 col-sm-12">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  whiteSpace: "nowrap",
                  flexWrap: "wrap"
                }}>
                <div className="col-sm-12 col-md-4">ຍອດລວມເປັນເງິນ (ກີບ): </div>
                <div
                  className="col-sm-12 col-md-8"
                  style={{
                    fontWeight: "bold",
                    padding: "5px 20px",
                    borderRadius: 10,
                    background: "#F5F5FA",
                    marginBottom: "10px"
                  }}>
                  {thousandSeperate(sumPriceLak)}
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  margin: "10px 0px",
                  whiteSpace: "nowrap",
                  flexWrap: "wrap"
                }}>
                <div className="col-sm-12 col-md-4">ຍອດລວມເປັນເງິນ (ບາດ): </div>
                <div
                  className="col-sm-12 col-md-8"
                  style={{
                    fontWeight: "bold",
                    padding: "5px 20px",
                    borderRadius: 10,
                    background: "#F5F5FA",
                    marginBottom: "10px"
                  }}>
                  {thousandSeperate(sumPriceBaht)}
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  whiteSpace: "nowrap",
                  flexWrap: "wrap"
                }}>
                <div className="col-sm-12 col-md-4">ຍອດລວມເປັນເງິນ (ໂດລາ): </div>
                <div
                  className="col-sm-12 col-md-8"
                  style={{
                    fontWeight: "bold",
                    padding: "5px 20px",
                    borderRadius: 10,
                    background: "#F5F5FA",
                    marginBottom: "10px"
                  }}>
                  {thousandSeperate(sumPriceUsd)}
                </div>
              </div>
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-md-6 col-sm-12"></div>
            <div className="col-md-6 col-sm-12 d-flex justify-content-end">
              <button
                className="btn me-3"
                style={{
                  background: "#E8EDF1",
                  padding: "8px 16px",
                  borderRadius: 4,
                  border: "none"
                }}
                onClick={() => history.push(`/orders/limit/30/page/1`)}>
                ຍົກເລີກ
              </button>

              <button
                className="btn"
                style={{
                  background: !orderList || orderList.length === 0 ? "#b0b0b0" : COLOR_APP,
                  padding: "8px 16px",
                  color: "white",
                  cursor: !orderList || orderList.length === 0 ? "not-allowed" : "pointer",
                  borderRadius: 4,
                  opacity: !orderList || orderList.length === 0 ? 0.7 : 1,
                  transition: "all 0.3s ease",
                  border: "none"
                }}
                onClick={() => {
                  if (orderList && orderList.length > 0) {
                    _saveData();
                  }
                }}
                disabled={!orderList || orderList.length === 0}
              >
                {
                  createOrderGroupV2Loading ||
                  updateOrderGroupV2Loading ? (
                  <Spinner size="sm" />
                ) : (
                  "ບັນທຶກ"
                )}
              </button>
            </div>
          </div>
          <div style={{ height: 20 }} />
        </div>
        <OrderAddModal 
          show={showOrder}
          onHide={() => setShowOrder(false)}
          stocksData={stocksData}
          onSelectStock={onSelectStock}
          onSelectStockItem={onSelectStockItem}
          cfCode={cfCode}
          setCfCode={setCfCode}
          productName={productName}
          setProductName={setProductName}
          amount={amount}
          setAmount={setAmount}
          stockAmount={stockAmount}
          price={price}
          setPrice={setPrice}
          totalPrice={totalPrice}
          setTotalPrice={setTotalPrice}
          addToast={addToast}
          currency={currency}
          onSave={() => _createOrderList()}
          onCancel={() => {
            setShowOrder(false);
            setCfCode("");
            setPrice(0);
            setOriginPrice(0);
            setAmount(1);
            setTotalPrice(0);
            setProductName("");
          }}
          loading={ createOrderGroupV2Loading || updateOrderGroupV2Loading}
        />
    </div>
  );
}

export default OrderAddV2;
